/**
 * svgInfoModeHelpers — pure DOM query helpers for InfoMode pointer interactions.
 *
 * Each function receives a raw event target (or a DOM element) and returns the
 * relevant node (or a derived primitive), or null when the target does not match
 * the expected element type.
 *
 * No React, no state, no side-effects — testable in isolation with mock objects.
 */

// ── Compartments ──────────────────────────────────────────────────────────────

/** Returns the closest path.compartment ancestor (or self), or null. */
export function getCompartmentPath(target) {
  return target?.closest?.('path.compartment') ?? null
}

/** Reads the `number` attribute from a compartment path as a Number, or null. */
export function getCompartmentNumber(path) {
  const n = path?.getAttribute?.('number')
  return n != null ? Number(n) : null
}

// ── Windows ───────────────────────────────────────────────────────────────────

/** Returns the closest g.window ancestor (SVG window-opening group), or null. */
export function getWindowGroup(target) {
  return target?.closest?.('g.window') ?? null
}

/** Reads the `id_hex` attribute from a window group, or null. */
export function getWindowIdHex(windowG) {
  return windowG?.getAttribute?.('id_hex') ?? null
}

/**
 * Looks up a window entry by id_hex across all tiers of a product.
 * Handles multi-level apartments by searching every tier.
 * Returns null when product or idHex is falsy, or when no match is found.
 */
export function findWindowByIdHex(product, idHex) {
  if (!product || !idHex) return null
  for (const tier of product.tiers ?? []) {
    const win = (tier.windows ?? []).find((w) => w.id_hex === idHex)
    if (win) return win
  }
  return null
}

// ── Equipment ─────────────────────────────────────────────────────────────────

/**
 * Returns a `use[href]` element when it is inside an .equipments group, or null.
 * Does NOT return use elements outside .equipments (e.g. pattern-fill refs).
 */
export function getEquipmentUse(target) {
  const useEl = target?.closest?.('use[href]') ?? null
  if (!useEl) return null
  return useEl.closest('[class~="equipments"]') ? useEl : null
}

/** Extracts the symbol id from a use element's href ("defs.svg#dc" → "dc"). */
export function getEquipmentUseId(useEl) {
  const href = useEl?.getAttribute?.('href') ?? ''
  return href.split('#')[1] ?? null
}

/** Extracts scale values from transform a use element's transform attribute. */
export function getEquipmentUseScale(useEl) {
  /* //Jeśli projekt wspiera nowoczesny standard geometryczny SVG, możesz użyć wbudowanego API zamiast parsowania tekstu:
  if (!useEl || !useEl.transform || useEl.transform.baseVal.numberOfItems === 0) return null
  
  // Pobiera pierwszą macierz transformacji
  const matrix = useEl.transform.baseVal.getItem(0).matrix
  
  return {
    x: matrix.a,
    y: matrix.d
  }
  */
  const fallback = { x: 1, y: 1 };

  const transform = useEl?.getAttribute?.('transform')
  if (!transform) return fallback;

  const match = transform.match(/scale\(([^)]+)\)/);
  if (!match) return fallback;

  const values = match[1].trim().split(/[\s,]+/).map(Number);
  const x = Number.isNaN(values[0]) ? 1 : Math.abs(values[0]);
  const y = values[1] !== undefined && !Number.isNaN(values[1]) ? Math.abs(values[1]) : x;

  return { x, y };
}

/**
 * Returns the closest casework group (e.g. `g.casework.kitchenette`), or null.
 * Returns null when target is inside a `use[href]` — casework and use are mutually exclusive.
 */
export function getCaseworkGroup(target) {
  if (target?.closest?.('use[href]')) return null
  return target?.closest?.('[class~="casework"]') ?? null
}

/** Extracts the casework type from the element's class list ("casework kitchenette" → "kitchenette"). */
export function getCaseworkType(el) {
  return Array.from(el?.classList ?? []).find((c) => c !== 'casework') ?? null
}

// ── Partitions ────────────────────────────────────────────────────────────────

/** Returns the target itself when it is a path.partition element, otherwise null. */
export function getPartitionPath(target) {
  return target?.classList?.contains?.('partition') ? target : null
}
